/**
 * Auth token/session storage — the T8.6 decision, hardened per D10.5
 * (docs/data-security-tasks.md; docs/planned_work/auth-permissions.md).
 *
 * DECISION: localStorage for the mock phase, with a single choke point so the
 * real implementation can switch to httpOnly cookies without touching call
 * sites. Rationale: this app is a static SPA (HashRouter, any static host) with
 * no backend yet — there is no origin to set cookies from. XSS risk is
 * mitigated for now by the dependency hygiene rule (no unvetted deps) and must
 * be revisited at backend integration (CSP, token TTL, refresh rotation).
 *
 * D10.5 hardening (this file):
 * - Every key carries the `agiOps:` app prefix — no ambiguous names shared
 *   with other apps on this origin, and greppable for the storage audit.
 * - writeStoredSession()/readStoredSession() carry an explicit tenantId and
 *   refuse to hand back a persisted session belonging to a different tenant
 *   (tenant isolation is a display-level duty too — D10.1). A mismatched
 *   record is dropped, never rendered.
 *
 * Trade-offs to revisit when the FastAPI backend exists:
 * - httpOnly + SameSite cookies defeat XSS token theft and need a same-site
 *   API origin or CSRF tokens for cross-origin calls.
 * - Keep every consumer going through readToken/writeToken/clearToken.
 */
const STORAGE_PREFIX = 'agiOps:';
const TOKEN_KEY = `${STORAGE_PREFIX}session.token`;
const SESSION_KEY = `${STORAGE_PREFIX}session`;

export function readToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Read alias for API-layer consumers (lib/api/http.js): same key, same null
 * contract, named for intent at call sites that attach the stored token to
 * outbound requests. Kept separate from readToken() so the storage internals
 * can change shape (httpOnly cookie swap) without touching the auth slice.
 */
export function getAuthToken() {
  return readToken();
}

export function writeToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage unavailable (private mode / quota) — session simply won't persist.
  }
}

export function clearToken() {
  writeToken(null);
}

/**
 * Read the persisted session for the given tenant. Returns null when:
 * - nothing is stored, the record is malformed, or storage is unavailable;
 * - the stored record belongs to a different tenantId (D10.1/D10.5 tenant
 *   scope check) — the stale record is dropped rather than displayed.
 */
export function readStoredSession(tenantId) {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (session?.tenantId && tenantId && session.tenantId !== tenantId) {
      // Different tenant on this browser profile — purge, report anonymous.
      window.localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function writeStoredSession(session, tenantId) {
  try {
    if (session && tenantId && session.tenantId && session.tenantId !== tenantId) {
      // Never persist a record that contradicts the active tenant.
      return;
    }
    if (session) window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Ignore — non-fatal.
  }
}

export function clearStoredSession() {
  writeStoredSession(null);
}

/**
 * One-time cleanup for the D10.5 rename: drop pre-prefix keys (agi.session,
 * agi.session.token) so stale records containing PII don't linger on browsers
 * that ran the old build. Safe to call on every hydrate.
 */
export function purgeLegacyKeys() {
  try {
    window.localStorage.removeItem('agi.session');
    window.localStorage.removeItem('agi.session.token');
  } catch {
    // Non-fatal.
  }
}
